import { useState } from 'react'
import {
  HiOutlineFire,
  HiOutlineClock,
  HiOutlineBookmark,
  HiBookmark,
  HiOutlineShare,
  HiOutlineNewspaper,
  HiOutlineExternalLink,
  HiOutlineUserGroup,
  HiOutlineTrendingUp,
} from 'react-icons/hi'
import { formatDistanceToNow } from 'date-fns'
import { vi } from 'date-fns/locale'
import clsx from 'clsx'

// Source branding config - colors and display info for each news source
const SOURCE_BRANDING = {
  // Major national sources
  vnexpress: { name: 'VnExpress', short: 'VNE', color: '#D32F2F', bg: 'from-red-500 to-red-600' },
  vneconomy: { name: 'VnEconomy', short: 'VNE', color: '#1565C0', bg: 'from-blue-600 to-blue-700' },
  vietnamnet: { name: 'VietnamNet', short: 'VNN', color: '#E65100', bg: 'from-orange-500 to-orange-600' },
  vietnamplus: { name: 'Vietnam+', short: 'VN+', color: '#C62828', bg: 'from-red-600 to-red-700' },
  vietnambiz: { name: 'VietnamBiz', short: 'VNB', color: '#00838F', bg: 'from-cyan-600 to-cyan-700' },
  tuoitre: { name: 'Tuổi Trẻ', short: 'TTO', color: '#0277BD', bg: 'from-blue-500 to-blue-600' },
  thanhnien: { name: 'Thanh Niên', short: 'TNO', color: '#D84315', bg: 'from-orange-600 to-red-600' },
  dantri: { name: 'Dân Trí', short: 'DT', color: '#1976D2', bg: 'from-blue-500 to-indigo-600' },

  // Real estate focused
  cafef: { name: 'CafeF', short: 'CF', color: '#F57C00', bg: 'from-orange-400 to-orange-500' },
  cafeland: { name: 'CafeLand', short: 'CL', color: '#43A047', bg: 'from-green-500 to-green-600' },
  batdongsan: { name: 'Batdongsan', short: 'BĐS', color: '#E53935', bg: 'from-red-500 to-red-600' },
  baomoi: { name: 'Báo Mới', short: 'BM', color: '#1E88E5', bg: 'from-blue-500 to-blue-600' },

  // Regional sources - Miền Trung
  baodanang: { name: 'Báo Đà Nẵng', short: 'ĐN', color: '#00897B', bg: 'from-teal-500 to-teal-600' },
  baoquangnam: { name: 'Báo Quảng Nam', short: 'QNa', color: '#5E35B1', bg: 'from-purple-500 to-purple-600' },
  baoquangngai: { name: 'Báo Quảng Ngãi', short: 'QNg', color: '#00ACC1', bg: 'from-cyan-500 to-cyan-600' },
  baoquangtri: { name: 'Báo Quảng Trị', short: 'QT', color: '#7CB342', bg: 'from-lime-500 to-green-500' },
  baoquangbinh: { name: 'Báo Quảng Bình', short: 'QB', color: '#039BE5', bg: 'from-sky-500 to-sky-600' },
  baothuathienhue: { name: 'Báo TT Huế', short: 'Huế', color: '#8E24AA', bg: 'from-purple-500 to-violet-600' },
  baobinhdinh: { name: 'Báo Bình Định', short: 'BĐ', color: '#F4511E', bg: 'from-orange-500 to-red-500' },
  baophuyen: { name: 'Báo Phú Yên', short: 'PY', color: '#00BCD4', bg: 'from-cyan-400 to-cyan-500' },
  baokhanhhoa: { name: 'Báo Khánh Hòa', short: 'KH', color: '#26A69A', bg: 'from-teal-400 to-teal-500' },

  // Tây Nguyên
  baogialai: { name: 'Báo Gia Lai', short: 'GL', color: '#66BB6A', bg: 'from-green-400 to-green-500' },
  baodaklak: { name: 'Báo Đắk Lắk', short: 'ĐL', color: '#AB47BC', bg: 'from-purple-400 to-purple-500' },
  baodaknong: { name: 'Báo Đắk Nông', short: 'ĐN', color: '#7E57C2', bg: 'from-violet-400 to-violet-500' },
  baokontum: { name: 'Báo Kon Tum', short: 'KT', color: '#26C6DA', bg: 'from-cyan-400 to-teal-400' },
  baolamdong: { name: 'Báo Lâm Đồng', short: 'LĐ', color: '#9CCC65', bg: 'from-lime-400 to-green-400' },

  // Google News (fallback for aggregated)
  googlenews: { name: 'Google News', short: 'GN', color: '#4285F4', bg: 'from-blue-400 to-blue-500' },
}

// Get source branding info
const getSourceBranding = (source) => {
  if (!source) return null
  const key = source.toLowerCase().replace(/[^a-z0-9]/g, '')
  return SOURCE_BRANDING[key] || null
}

// ImageWithFallback component - shows source logo when no image
function ImageWithFallback({ src, alt = '', className, size = 'md', source = '' }) {
  const [error, setError] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const showPlaceholder = !src || error
  const branding = getSourceBranding(source)

  return (
    <div className={clsx('relative overflow-hidden', className)}>
      {showPlaceholder ? (
        <div className={clsx(
          'absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br',
          branding?.bg || 'from-secondary-400 to-secondary-500'
        )}>
          {/* Source logo/text */}
          <div className={clsx(
            'flex items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm',
            size === 'lg' ? 'w-20 h-20' : size === 'md' ? 'w-14 h-14' : 'w-10 h-10'
          )}>
            <span className={clsx(
              'font-black text-white tracking-tight',
              size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-sm'
            )}>
              {branding?.short || 'BĐS'}
            </span>
          </div>
          {/* Source name */}
          <span className={clsx(
            'text-white/90 font-medium mt-2 px-2 text-center',
            size === 'lg' ? 'text-sm' : 'text-xs'
          )}>
            {branding?.name || 'Tin BĐS'}
          </span>
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-2 right-2">
              <HiOutlineNewspaper className={size === 'lg' ? 'w-16 h-16' : 'w-10 h-10'} />
            </div>
            <div className="absolute bottom-2 left-2">
              <HiOutlineNewspaper className={size === 'lg' ? 'w-12 h-12' : 'w-8 h-8'} />
            </div>
          </div>
        </div>
      ) : (
        <>
          {!loaded && (
            <div className="absolute inset-0 animate-pulse bg-secondary-200" />
          )}
          <img
            src={src}
            alt={alt}
            className={clsx(
              'w-full h-full object-cover transition-opacity',
              loaded ? 'opacity-100' : 'opacity-0'
            )}
            onError={() => setError(true)}
            onLoad={() => setLoaded(true)}
          />
        </>
      )}
    </div>
  )
}

// Safe time formatter
const formatTime = (dateStr) => {
  if (!dateStr) return ''
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: vi })
  } catch {
    return ''
  }
}

// Get source name safely
const getSourceName = (news) => {
  return news.source_name ||
    (typeof news.source === 'object' ? news.source?.name : news.source) ||
    'Nguồn tin'
}

// Get location name safely
const getLocationName = (news) => {
  return news.location_name ||
    (typeof news.location === 'object' ? news.location?.name : news.location) ||
    ''
}

// Get customer type badge
const getCustomerTypeBadge = (customerType) => {
  switch (customerType) {
    case 'dau_tu':
      return { label: 'Đầu tư', color: 'bg-emerald-100 text-emerald-700', icon: HiOutlineTrendingUp }
    case 'mua_o':
      return { label: 'Mua ở', color: 'bg-blue-100 text-blue-700', icon: HiOutlineUserGroup }
    default:
      return null
  }
}

// Featured Card - Hero style for internal tool
function FeaturedCard({ news, onSave, onShare, isSaved = false }) {
  const customerBadge = getCustomerTypeBadge(news.customer_type)
  const imageUrl = news.thumbnail_url || news.image_url
  const BookmarkIcon = isSaved ? HiBookmark : HiOutlineBookmark

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        {/* Image - Link to original article */}
        <a
          href={news.url}
          target="_blank"
          rel="noopener noreferrer"
          className="lg:w-1/2 relative block group"
        >
          <ImageWithFallback
            src={imageUrl}
            className="aspect-video lg:aspect-auto lg:h-full lg:min-h-[280px] group-hover:scale-105 transition-transform duration-300"
            size="lg"
            source={news.source}
          />
          {/* Overlay badges */}
          <div className="absolute top-3 left-3 flex gap-2">
            {news.is_hot && (
              <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow">
                <HiOutlineFire className="w-3.5 h-3.5" />
                HOT
              </span>
            )}
          </div>
        </a>

        {/* Content */}
        <div className="lg:w-1/2 p-5 flex flex-col">
          {/* Meta */}
          <div className="flex items-center gap-2 text-xs text-secondary-500 mb-3">
            <span className="font-semibold text-primary-600">{getSourceName(news)}</span>
            {getLocationName(news) && (
              <>
                <span>•</span>
                <span>{getLocationName(news)}</span>
              </>
            )}
            <span>•</span>
            <span className="flex items-center gap-1">
              <HiOutlineClock className="w-3.5 h-3.5" />
              {formatTime(news.published_at)}
            </span>
          </div>

          {/* Title - Link to original article */}
          <a
            href={news.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group"
          >
            <h2 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
              {news.title}
            </h2>
          </a>

          {/* Summary */}
          {news.summary && (
            <div className="flex-1 mb-4">
              <p className="text-sm text-secondary-600 line-clamp-3">
                {news.summary}
              </p>
            </div>
          )}

          {/* Customer type & Sentiment */}
          <div className="flex items-center gap-2 mb-4">
            {customerBadge && (
              <span className={clsx('px-2 py-1 text-xs font-medium rounded-lg flex items-center gap-1', customerBadge.color)}>
                <customerBadge.icon className="w-3.5 h-3.5" />
                {customerBadge.label}
              </span>
            )}
            {news.sentiment === 'positive' && (
              <span className="px-2 py-1 text-xs font-medium rounded-lg bg-green-100 text-green-700">
                Tích cực
              </span>
            )}
            {news.sentiment === 'negative' && (
              <span className="px-2 py-1 text-xs font-medium rounded-lg bg-red-100 text-red-700">
                Tiêu cực
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-secondary-100">
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => onSave?.(news.id)}
                className={clsx(
                  'p-2.5 sm:p-2 rounded-lg transition-colors',
                  isSaved
                    ? 'bg-primary-100 text-primary-600'
                    : 'hover:bg-secondary-100 active:bg-secondary-200 text-secondary-500 hover:text-primary-600'
                )}
                title={isSaved ? 'Đã lưu' : 'Lưu tin'}
              >
                <BookmarkIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => onShare?.(news.id)}
                className="p-2.5 sm:p-2 rounded-lg hover:bg-secondary-100 active:bg-secondary-200 transition-colors text-secondary-500 hover:text-primary-600"
                title="Chia sẻ"
              >
                <HiOutlineShare className="w-5 h-5" />
              </button>
            </div>
            <a
              href={news.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm px-3 py-2 sm:px-4"
            >
              <HiOutlineExternalLink className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Đọc bài gốc</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// Compact Card for list view
function CompactCard({ news, onSave, onShare, isSaved = false }) {
  const customerBadge = getCustomerTypeBadge(news.customer_type)
  const imageUrl = news.thumbnail_url || news.image_url
  const BookmarkIcon = isSaved ? HiBookmark : HiOutlineBookmark

  return (
    <div className="card overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex">
        {/* Thumbnail - Link to original article */}
        <a
          href={news.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-32 sm:w-40 flex-shrink-0 relative block group"
        >
          <ImageWithFallback
            src={imageUrl}
            className="w-full h-full min-h-[120px] group-hover:scale-105 transition-transform duration-300"
            size="sm"
            source={news.source}
          />
          {news.is_hot && (
            <div className="absolute top-2 left-2">
              <span className="w-6 h-6 bg-red-500 rounded-md flex items-center justify-center shadow">
                <HiOutlineFire className="w-4 h-4 text-white" />
              </span>
            </div>
          )}
        </a>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col min-w-0">
          {/* Meta */}
          <div className="flex items-center gap-2 text-xs text-secondary-500 mb-1.5">
            <span className="font-medium text-primary-600 truncate">{getSourceName(news)}</span>
            {getLocationName(news) && (
              <>
                <span className="flex-shrink-0">•</span>
                <span className="truncate">{getLocationName(news)}</span>
              </>
            )}
          </div>

          {/* Title - Link to original article */}
          <a
            href={news.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex-1"
          >
            <h3 className="font-semibold text-secondary-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
              {news.title}
            </h3>
          </a>

          {/* Bottom row */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              {customerBadge && (
                <span className={clsx('px-1.5 py-0.5 text-2xs font-medium rounded', customerBadge.color)}>
                  {customerBadge.label}
                </span>
              )}
              <span className="text-2xs text-secondary-400 flex items-center gap-1">
                <HiOutlineClock className="w-3 h-3" />
                {formatTime(news.published_at)}
              </span>
            </div>

            {/* Quick actions */}
            <div className="flex items-center">
              <button
                onClick={() => onSave?.(news.id)}
                className={clsx(
                  'p-2 sm:p-1.5 rounded-lg transition-colors',
                  isSaved
                    ? 'bg-primary-100 text-primary-600'
                    : 'hover:bg-secondary-100 active:bg-secondary-200 text-secondary-400 hover:text-primary-600'
                )}
                title={isSaved ? 'Đã lưu' : 'Lưu tin'}
              >
                <BookmarkIcon className="w-5 h-5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Grid Card for tile view
function GridCard({ news, onSave, isSaved = false }) {
  const imageUrl = news.thumbnail_url || news.image_url

  return (
    <div className="card overflow-hidden hover:shadow-md transition-shadow group relative">
      {/* Image - Link to original article */}
      <a
        href={news.url}
        target="_blank"
        rel="noopener noreferrer"
        className="relative aspect-video block overflow-hidden"
      >
        <ImageWithFallback
          src={imageUrl}
          className="w-full h-full group-hover:scale-105 transition-transform duration-300"
          size="md"
          source={news.source}
        />
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          {news.is_hot && (
            <span className="px-1.5 py-0.5 bg-red-500 text-white text-2xs font-bold rounded flex items-center gap-0.5 shadow">
              <HiOutlineFire className="w-3 h-3" />
              HOT
            </span>
          )}
        </div>
      </a>
      {/* Save button - always visible on mobile, hover on desktop */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSave?.(news.id)
        }}
        className={clsx(
          'absolute top-2 right-2 p-2 sm:p-1.5 rounded-lg shadow transition-all z-10',
          isSaved
            ? 'bg-primary-500 text-white opacity-100'
            : 'bg-white/90 text-secondary-500 hover:text-primary-600 active:bg-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100'
        )}
        title={isSaved ? 'Đã lưu' : 'Lưu tin'}
      >
        {isSaved ? (
          <HiBookmark className="w-5 h-5 sm:w-4 sm:h-4" />
        ) : (
          <HiOutlineBookmark className="w-5 h-5 sm:w-4 sm:h-4" />
        )}
      </button>

      {/* Content */}
      <div className="p-3">
        {/* Meta */}
        <div className="flex items-center gap-1.5 text-2xs text-secondary-500 mb-1.5">
          <span className="font-medium text-primary-600 truncate">{getSourceName(news)}</span>
          <span>•</span>
          <span className="truncate">{formatTime(news.published_at)}</span>
        </div>

        {/* Title - Link to original article */}
        <a
          href={news.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group/link"
        >
          <h3 className="font-semibold text-secondary-900 text-sm line-clamp-2 min-h-[40px] group-hover/link:text-primary-600 transition-colors">
            {news.title}
          </h3>
        </a>
      </div>
    </div>
  )
}

// Main NewsGrid component
function NewsGrid({ news = [], loading = false, onSave, onShare, savedNewsIds = [], newItemsStartIndex = null, newContentRef = null }) {
  // Convert to Set for O(1) lookup
  const savedSet = new Set(savedNewsIds)

  if (loading) {
    return <NewsGridSkeleton />
  }

  if (!news || news.length === 0) {
    return (
      <div className="card p-12 text-center">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mb-4">
            <HiOutlineNewspaper className="w-8 h-8 text-secondary-400" />
          </div>
          <p className="font-semibold text-secondary-700 mb-1">Không tìm thấy tin tức</p>
          <p className="text-sm text-secondary-500">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
        </div>
      </div>
    )
  }

  // Layout: 1 featured + rest in grid
  const featured = news[0]
  const gridItems = news.slice(1, 7) // Next 6 items in grid
  const listItems = news.slice(7) // Rest in compact list

  // Check if an item is newly loaded (for animation)
  const isNewItem = (globalIndex) => {
    return newItemsStartIndex !== null && globalIndex >= newItemsStartIndex
  }

  return (
    <div className="space-y-6">
      {/* Featured news */}
      <FeaturedCard news={featured} onSave={onSave} onShare={onShare} isSaved={savedSet.has(featured.id)} />

      {/* Grid section */}
      {gridItems.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {gridItems.map((item, index) => {
            const globalIndex = index + 1 // +1 for featured
            const isNew = isNewItem(globalIndex)
            return (
              <div
                key={item.id}
                ref={isNew && globalIndex === newItemsStartIndex ? newContentRef : null}
                className={isNew ? 'animate-slide-in-up' : ''}
                style={isNew ? { animationDelay: `${(globalIndex - newItemsStartIndex) * 80}ms` } : {}}
              >
                <GridCard news={item} onSave={onSave} isSaved={savedSet.has(item.id)} />
              </div>
            )
          })}
        </div>
      )}

      {/* List section */}
      {listItems.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-secondary-700 flex items-center gap-2">
            <HiOutlineClock className="w-5 h-5 text-secondary-400" />
            Tin mới nhất
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {listItems.map((item, index) => {
              const globalIndex = index + 7 // +1 featured +6 grid
              const isNew = isNewItem(globalIndex)
              return (
                <div
                  key={item.id}
                  ref={isNew && globalIndex === newItemsStartIndex ? newContentRef : null}
                  className={isNew ? 'animate-slide-in-up' : ''}
                  style={isNew ? { animationDelay: `${(globalIndex - newItemsStartIndex) * 60}ms` } : {}}
                >
                  <CompactCard news={item} onSave={onSave} onShare={onShare} isSaved={savedSet.has(item.id)} />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  )
}

// Loading skeleton
function NewsGridSkeleton() {
  return (
    <div className="space-y-6">
      {/* Featured skeleton */}
      <div className="card overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-1/2 aspect-video lg:aspect-auto lg:min-h-[280px] skeleton" />
          <div className="lg:w-1/2 p-5 space-y-4">
            <div className="skeleton h-4 w-1/3 rounded" />
            <div className="skeleton h-6 w-full rounded" />
            <div className="skeleton h-6 w-3/4 rounded" />
            <div className="skeleton h-16 w-full rounded" />
            <div className="flex gap-2 pt-4">
              <div className="skeleton h-10 w-24 rounded-lg" />
              <div className="skeleton h-10 w-32 rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Grid skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="card overflow-hidden">
            <div className="aspect-video skeleton" />
            <div className="p-3 space-y-2">
              <div className="skeleton h-3 w-1/2 rounded" />
              <div className="skeleton h-4 w-full rounded" />
              <div className="skeleton h-4 w-3/4 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default NewsGrid
